import { PaymentProvider, SubscriptionStatus } from "@brookbush/database";
import { type SubscriptionExpanded, UserAction } from "@brookbush/types";

import { useUser } from "@clerk/nextjs";
import {
	ExpressCheckoutElement,
	PaymentElement,
	useElements,
	useStripe,
} from "@stripe/react-stripe-js";
import type {
	PaymentIntent,
	PaymentIntentResult,
	SetupIntent,
	SetupIntentResult,
	StripeError,
} from "@stripe/stripe-js";
import type { ApplePayRecurringPaymentRequestIntervalUnit } from "@stripe/stripe-js/dist/stripe-js/elements/apple-pay";
import clsx from "clsx";
import { ChevronLeftIcon, CreditCard } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FaCheckCircle, FaCircle, FaInfoCircle } from "react-icons/fa";
import type Stripe from "stripe";

import BecomeAMemberCheckoutPayPalButton from "./BecomeAMemberCheckoutPayPalButton";
import Button from "~/components/Button";
import Skeleton from "~/components/Skeleton";
import {
	useStripePaymentMethods,
	useStripeSubscription,
	useSubscriptionDB,
	useUserAction,
} from "~/hooks";
import useCheckout from "~/hooks/useCheckout";
import useConversionFlag from "~/hooks/useConversionFlag";
import useJoiningPrice from "~/hooks/useJoiningPrice";
import usePaymentElementLoadState from "~/hooks/usePaymentElementLoadState";
import usePayPalSubscription from "~/hooks/usePayPalSubscription";
import usePlanConfig from "~/hooks/usePlanConfig";
import useSale from "~/hooks/useSale";
import useStripePrices from "~/hooks/useStripePrices";
import type { BecomeAMemberSurface } from "~/lib/becomeAMemberTracking";
import captureBecomeAMemberEvent from "~/lib/captureBecomeAMemberEvent";
import {
	CONVERSION_FLAGS,
	isConversionFlagOn,
} from "~/lib/conversion-test-flags";
import {
	getApplicableJoiningFee,
	getSelectedPlanJoiningCopy,
	getTrialEndsLabel,
} from "~/lib/membership-joining-price";
import { trackPurchasePixelEvent } from "~/lib/pixels.lib";
import {
	canUsePayPalCheckout,
	shouldOpenNewCardFormAfterDecline,
} from "~/lib/stripe/checkoutPaymentGuards";
import {
	AUTH_FAILURE_MESSAGE,
	getPaymentFailureDetails,
	isRecoverablePaymentFailure,
} from "~/lib/stripe/getPaymentErrorMessage";
import {
	convertToCents,
	getPaymentMethodIdentifier,
	getTotalAmountDue,
} from "~/lib/stripe/helpers";
import {
	getPaymentElementLoadErrorDetails,
	getPaymentElementLoadErrorMessage,
} from "~/lib/stripe/paymentElementLoadState";
import { resolveSelectedPaymentMethod } from "~/lib/stripe/resolveSelectedPaymentMethod";
import {
	type CheckoutSubmitSource,
	shouldBlockCheckoutForPaymentElement,
} from "~/lib/stripe/shouldBlockCheckoutForPaymentElement";

const BecomeAMemberCheckoutPaymentForm = () => {
	const stripe = useStripe();
	const elements = useElements();

	const { refreshSubscriptionDB } = useSubscriptionDB();
	const { paypalSubscription } = usePayPalSubscription();

	const hasActivePayPalSubscription =
		!!paypalSubscription &&
		paypalSubscription.status === SubscriptionStatus.ACTIVE;

	const { planConfig } = usePlanConfig();

	const {
		flags: { isChangingPlan },
		completeCheckout,
		setPaymentSubmitting,
	} = useCheckout();

	const [loading, setLoading] = useState(false);

	const { handleUpgraded, sale } = useSale();
	const [errorMessage, setErrorMessage] = useState<string>();

	const [declinedPaymentMethod, setDeclinedPaymentMethod] =
		useState<Stripe.PaymentMethod | null>(null);

	const {
		createStripeSubscription,
		changeStripeSubscription,
		isActive: isStripeActive,
	} = useStripeSubscription();

	const { user } = useUser();
	const { userAction } = useUserAction();
	const surface: BecomeAMemberSurface =
		userAction === UserAction.BECOME_A_MEMBER ? "modal" : "page";

	const { prices } = useStripePrices(planConfig.planType);
	const { offer } = useJoiningPrice();
	const joiningFee = isChangingPlan
		? 0
		: getApplicableJoiningFee(planConfig, offer);
	const joiningCopy = isChangingPlan
		? null
		: getSelectedPlanJoiningCopy({
				planConfig,
				prices,
				offer,
				trialEndsLabel: getTrialEndsLabel(planConfig.trialLength),
			});
	const payButtonLabel = joiningCopy?.button ?? "Click To Pay";

	const capturePlanProperties = {
		surface,
		interval: planConfig.price?.recurring?.interval,
		promo_code: planConfig.promoCode?.code,
		price_id: planConfig.price?.id,
		amount_due: planConfig.price
			? getTotalAmountDue(planConfig, joiningFee)
			: undefined,
	};

	const { defaultPaymentMethod, paymentMethods } = useStripePaymentMethods(
		planConfig.planType,
	);

	const [selectedPaymentMethod, setSelectedPaymentMethod] =
		useState<Stripe.PaymentMethod | null>(defaultPaymentMethod ?? null);

	const [isAddingNewPaymentMethod, setIsAddingNewPaymentMethod] =
		useState(false);

	const canUsePayPal = canUsePayPalCheckout({
		paypalPlanId: planConfig.price?.paypalPlanId,
		paypalPlanAmount: planConfig.price?.paypalPlanAmount,
		priceAmount: planConfig.price?.unit_amount,
		hasSale: !!sale,
		hasPromoCode: !!planConfig.promoCode,
		hasTrial: !!planConfig.trialLength,
		isStripeActive: !!isStripeActive,
		hasJoiningFee: joiningFee > 0,
	});

	const {
		remountKey: paymentElementRemountKey,
		isReady: isPaymentElementReady,
		isError: isPaymentElementError,
		isLoading: isPaymentElementLoading,
		onReady: onPaymentElementReady,
		onLoadError: onPaymentElementLoadError,
		retry: retryPaymentElement,
	} = usePaymentElementLoadState({
		enabled: isAddingNewPaymentMethod,
		isStripeReady: !!stripe && !!elements,
		onBlocked: (code, error) => {
			const message = getPaymentElementLoadErrorMessage({ canUsePayPal });
			captureBecomeAMemberEvent("checkout_blocked", {
				...capturePlanProperties,
				...getPaymentElementLoadErrorDetails(error),
				error_code: code,
				error_message: message,
			});
		},
	});

	const { value: trackingFlag } = useConversionFlag(
		CONVERSION_FLAGS.conversionTracking,
		{ enabled: isAddingNewPaymentMethod },
	);
	const paymentElementLoadStartRef = useRef(0);

	// biome-ignore lint/correctness/useExhaustiveDependencies: restart the timer on each PaymentElement remount
	useEffect(() => {
		paymentElementLoadStartRef.current = Date.now();
	}, [paymentElementRemountKey]);

	const handlePaymentElementReady = () => {
		onPaymentElementReady();

		if (isConversionFlagOn(trackingFlag)) {
			captureBecomeAMemberEvent("payment_form_ready", {
				...capturePlanProperties,
				load_ms: Date.now() - paymentElementLoadStartRef.current,
			});
		}
	};

	// First-time buyers: show the card form immediately instead of an empty
	// Express Checkout + "Pay With Card" wall with a long blank wait after click.
	useEffect(() => {
		if (
			paymentMethods &&
			paymentMethods.length === 0 &&
			!hasActivePayPalSubscription
		) {
			setIsAddingNewPaymentMethod(true);
			setSelectedPaymentMethod(null);
		}
	}, [hasActivePayPalSubscription, paymentMethods]);

	useEffect(() => {
		if (isAddingNewPaymentMethod) return;

		const nextPaymentMethod = resolveSelectedPaymentMethod({
			paymentMethods: paymentMethods ?? [],
			selected: selectedPaymentMethod,
			defaultPaymentMethod,
			declinedPaymentMethodId: declinedPaymentMethod?.id,
		});

		if (nextPaymentMethod?.id !== selectedPaymentMethod?.id) {
			setSelectedPaymentMethod(nextPaymentMethod);
		}
	}, [
		declinedPaymentMethod?.id,
		defaultPaymentMethod,
		isAddingNewPaymentMethod,
		paymentMethods,
		selectedPaymentMethod,
	]);

	const openNewPaymentMethod = () => {
		setSelectedPaymentMethod(null);
		setIsAddingNewPaymentMethod(true);
	};

	const promptDifferentPaymentMethod = (message: string) => {
		setLoading(false);
		setPaymentSubmitting(false);
		setErrorMessage(message);
		if (selectedPaymentMethod) {
			setDeclinedPaymentMethod(selectedPaymentMethod);
		}
		if (
			shouldOpenNewCardFormAfterDecline({
				alreadyAddingNewPaymentMethod: isAddingNewPaymentMethod,
			})
		) {
			openNewPaymentMethod();
		}
	};

	const handleError = (error: StripeError) => {
		const details = getPaymentFailureDetails({ stripeError: error });
		captureBecomeAMemberEvent("payment_failed", {
			...capturePlanProperties,
			...details,
		});

		if (isRecoverablePaymentFailure({ stripeError: error })) {
			promptDifferentPaymentMethod(details.error_message);
			return;
		}

		setLoading(false);
		setPaymentSubmitting(false);
		setErrorMessage(details.error_message);
	};

	const confirmMembership = async (
		subscriptionId: string,
		status: SubscriptionStatus,
		stripeStatus: string,
	) => {
		const nextSubscription = {
			id: subscriptionId,
			status,
			planType: planConfig.planType,
			createdAt: new Date(),
			paymentProvider: PaymentProvider.STRIPE,
			userId: user!.id,
			isPaused: false,
		} as SubscriptionExpanded;

		captureBecomeAMemberEvent("checkout_confirmed", {
			...capturePlanProperties,
			subscription_status: stripeStatus,
		});
		// Flip success UI immediately — don't leave "Processing…" / faded form
		// while subscription DB refresh finishes.
		completeCheckout("stripe");
		setLoading(false);
		setPaymentSubmitting(false);

		await refreshSubscriptionDB(nextSubscription, { revalidate: false });
	};

	const completeRequiresAction = async (
		clientSecret: string,
		subscriptionId: string,
		kind: "payment" | "setup",
	) => {
		try {
			const response = await stripe!.handleNextAction({ clientSecret });

			if (response.error) {
				handleError(response.error);
				return;
			}

			if (
				kind === "payment" &&
				response.paymentIntent?.status === "succeeded"
			) {
				await confirmMembership(
					subscriptionId,
					SubscriptionStatus.ACTIVE,
					response.paymentIntent.status,
				);
				if (planConfig.price?.recurring?.interval === "year") {
					handleUpgraded();
				}
				trackPurchasePixelEvent(user!, planConfig);
				return;
			}

			if (kind === "setup" && response.setupIntent?.status === "succeeded") {
				await confirmMembership(
					subscriptionId,
					SubscriptionStatus.TRIALING,
					response.setupIntent.status,
				);
				if (planConfig.price?.recurring?.interval === "year") {
					handleUpgraded();
				}
				trackPurchasePixelEvent(user!, planConfig);
				return;
			}

			promptDifferentPaymentMethod(AUTH_FAILURE_MESSAGE);
			captureBecomeAMemberEvent("payment_failed", {
				...capturePlanProperties,
				error_code: "authentication_required",
				error_message: AUTH_FAILURE_MESSAGE,
			});
		} catch (_error) {
			promptDifferentPaymentMethod(AUTH_FAILURE_MESSAGE);
			captureBecomeAMemberEvent("payment_failed", {
				...capturePlanProperties,
				error_code: "authentication_required",
				error_message: AUTH_FAILURE_MESSAGE,
			});
		}
	};

	const handleSubmit = async (submitSource: CheckoutSubmitSource = "card") => {
		const isUsingPaymentForm = !selectedPaymentMethod;

		if (!stripe || !elements) {
			const message =
				"The payment form is still loading. Wait a moment, then try again.";
			setErrorMessage(message);
			captureBecomeAMemberEvent("checkout_blocked", {
				...capturePlanProperties,
				error_code: "stripe_not_ready",
				error_message: message,
			});
			return;
		}

		if (
			shouldBlockCheckoutForPaymentElement({
				submitSource,
				hasSelectedPaymentMethod: !!selectedPaymentMethod,
				isChangingPlan: !!isChangingPlan,
				isPaymentElementReady,
			})
		) {
			const message =
				"The card form is still loading. Wait a moment, then try again.";
			setErrorMessage(message);
			captureBecomeAMemberEvent("checkout_blocked", {
				...capturePlanProperties,
				error_code: "payment_element_not_ready",
				error_message: message,
			});
			return;
		}

		setLoading(true);
		setPaymentSubmitting(true);
		setErrorMessage(undefined);

		if (surface === "modal") {
			captureBecomeAMemberEvent("checkout_started", capturePlanProperties);
		}

		if (isUsingPaymentForm && !isChangingPlan) {
			const { error: submitError } = await elements.submit();
			if (submitError) {
				handleError(submitError);
				return;
			}
		}

		try {
			if (isChangingPlan) {
				const updated = await changeStripeSubscription(planConfig);
				const appliedPriceId = updated.items?.data?.[0]?.price?.id;

				if (appliedPriceId && appliedPriceId !== planConfig.priceId) {
					setLoading(false);
					setPaymentSubmitting(false);
					const message = "Your plan did not update. Please try again.";
					setErrorMessage(message);
					captureBecomeAMemberEvent("payment_failed", {
						...capturePlanProperties,
						error_code: "plan_price_mismatch",
						error_message: message,
					});
					return;
				}

				captureBecomeAMemberEvent("checkout_confirmed", {
					...capturePlanProperties,
					subscription_status: updated.status,
				});
				completeCheckout("stripe");
				setLoading(false);
				if (planConfig.price?.recurring?.interval === "year") {
					handleUpgraded();
				}
			} else {
				const subscription = await createStripeSubscription({
					planConfig,
					paymentMethod: selectedPaymentMethod ?? undefined,
					surface: surface === "modal" ? "modal" : "page",
				});

				if (!isUsingPaymentForm) {
					const paymentIntent = (subscription.latest_invoice as Stripe.Invoice)
						.payment_intent as PaymentIntent | null;

					if (
						subscription.status === "active" ||
						subscription.status === "trialing"
					) {
						await confirmMembership(
							subscription.id,
							subscription.status === "active"
								? SubscriptionStatus.ACTIVE
								: SubscriptionStatus.TRIALING,
							subscription.status,
						);

						if (planConfig.price?.recurring?.interval === "year") {
							handleUpgraded();
						}
					} else if (
						paymentIntent?.status === "requires_action" &&
						paymentIntent.client_secret
					) {
						await completeRequiresAction(
							paymentIntent.client_secret,
							subscription.id,
							"payment",
						);
					} else {
						const details = getPaymentFailureDetails({
							code: paymentIntent?.last_payment_error?.code,
							declineCode: paymentIntent?.last_payment_error?.decline_code,
							message: paymentIntent?.last_payment_error?.message,
							fallbackCode: paymentIntent?.status,
						});
						promptDifferentPaymentMethod(details.error_message);
						captureBecomeAMemberEvent("payment_failed", {
							...capturePlanProperties,
							...details,
						});
					}
				}

				if (isUsingPaymentForm) {
					const paymentSetup = subscription.pending_setup_intent as
						| SetupIntent
						| undefined;

					const confirmIntent = paymentSetup
						? stripe.confirmSetup
						: stripe.confirmPayment;

					const { error, paymentIntent, setupIntent } = (await confirmIntent({
						elements,
						clientSecret: paymentSetup
							? paymentSetup.client_secret!
							: (
									(subscription.latest_invoice as Stripe.Invoice)
										.payment_intent as PaymentIntent
								).client_secret!,
						confirmParams: {
							return_url: `${window.location.origin}/become-a-member/processing`,
						},
						redirect: "if_required",
					})) as SetupIntentResult & PaymentIntentResult;

					if (error) {
						handleError(error);
					} else if (paymentIntent?.status === "succeeded") {
						await confirmMembership(
							subscription.id,
							SubscriptionStatus.ACTIVE,
							paymentIntent.status,
						);

						trackPurchasePixelEvent(user!, planConfig);
					} else if (setupIntent?.status === "succeeded") {
						await confirmMembership(
							subscription.id,
							SubscriptionStatus.TRIALING,
							setupIntent.status,
						);
						if (planConfig.price?.recurring?.interval === "year") {
							handleUpgraded();
						}

						trackPurchasePixelEvent(user!, planConfig);
					} else if (
						paymentIntent?.status === "requires_action" &&
						paymentIntent.client_secret
					) {
						await completeRequiresAction(
							paymentIntent.client_secret,
							subscription.id,
							"payment",
						);
					} else if (
						setupIntent?.status === "requires_action" &&
						setupIntent.client_secret
					) {
						await completeRequiresAction(
							setupIntent.client_secret,
							subscription.id,
							"setup",
						);
					} else {
						const lastError =
							paymentIntent?.last_payment_error ||
							setupIntent?.last_setup_error;
						const details = getPaymentFailureDetails({
							code: lastError?.code,
							declineCode: lastError?.decline_code,
							message: lastError?.message,
							fallbackCode: paymentIntent?.status || setupIntent?.status,
						});
						promptDifferentPaymentMethod(details.error_message);
						captureBecomeAMemberEvent("payment_failed", {
							...capturePlanProperties,
							...details,
						});
					}
				}
			}
		} catch (error: any) {
			const code = error.response?.data?.code as string | undefined;
			const declineCode = error.response?.data?.decline_code as
				| string
				| undefined;
			const details = getPaymentFailureDetails({
				code,
				declineCode,
				message: error.response?.data?.message,
				httpStatus: error.response?.status,
			});

			captureBecomeAMemberEvent("payment_failed", {
				...capturePlanProperties,
				...details,
			});

			if (
				isRecoverablePaymentFailure({ code, declineCode }) ||
				error.response?.data?.type === "card_error" ||
				error.response?.data?.type === "StripeCardError"
			) {
				if (selectedPaymentMethod) {
					setDeclinedPaymentMethod(selectedPaymentMethod);
				}
				promptDifferentPaymentMethod(details.error_message);
				return;
			}

			setLoading(false);
			setPaymentSubmitting(false);
			setErrorMessage(details.error_message);
		}
	};
	const showSavedPaymentMethods =
		!isAddingNewPaymentMethod &&
		!hasActivePayPalSubscription &&
		!!paymentMethods?.length;

	return (
		<form
			className={clsx("flex flex-1 basis-1/2 flex-col gap-4", {
				"opacity-50": loading,
			})}
			onSubmit={(e) => {
				e.preventDefault();
				void handleSubmit("card");
			}}
		>
			{!isAddingNewPaymentMethod && !hasActivePayPalSubscription && (
				<>
					{showSavedPaymentMethods && (
						<>
							{paymentMethods?.map((paymentMethod) => (
								<button
									disabled={loading}
									key={paymentMethod.id}
									type="button"
									onClick={() => setSelectedPaymentMethod(paymentMethod)}
									className={clsx(
										"flex w-full items-center gap-2 rounded-xl border-2 px-4 py-2 text-left font-medium text-lg hover:bg-slate-50 dark:hover:bg-slate-700/70",
										declinedPaymentMethod?.id === paymentMethod.id
											? "border-red-600/40"
											: paymentMethod.id === selectedPaymentMethod?.id
												? "border-emerald-400"
												: "border-slate-200 dark:border-slate-600",
									)}
								>
									{declinedPaymentMethod?.id === paymentMethod.id ? (
										<FaInfoCircle size={19} className="text-red-600" />
									) : paymentMethod.id === selectedPaymentMethod?.id ? (
										<FaCheckCircle size={19} className="text-emerald-500" />
									) : (
										<FaCircle
											size={19}
											className="rounded-full border-2 border-slate-200 text-transparent dark:border-slate-600"
											strokeWidth={2.3}
										/>
									)}{" "}
									{getPaymentMethodIdentifier(paymentMethod)}
								</button>
							))}

							<Button
								className="w-full"
								type="submit"
								disabled={loading || !selectedPaymentMethod}
							>
								{payButtonLabel}
							</Button>

							<div className="flex w-full items-center justify-center gap-4 text-slate-500 text-sm dark:text-slate-300">
								<div className="flex-1 border-t" />
								OR
								<div className="flex-1 border-t" />
							</div>
						</>
					)}

					<ExpressCheckoutElement
						options={{
							layout: {
								overflow: "never",
								maxColumns: 1,
							},

							paymentMethodOrder: ["googlePay", "applePay"],
						}}
						onClick={({ resolve }) => {
							resolve({
								emailRequired: true,
								applePay: {
									recurringPaymentRequest: {
										paymentDescription: planConfig.price!.nickname!,
										managementURL: "https://brookbushinstitute.com/membership",
										regularBilling: {
											amount: convertToCents(getTotalAmountDue(planConfig)),
											label: planConfig.price!.nickname!,
											recurringPaymentIntervalUnit: planConfig.price?.recurring
												?.interval as ApplePayRecurringPaymentRequestIntervalUnit,
											recurringPaymentIntervalCount: 1,
										},
									},
								},
							});
						}}
						onConfirm={() => {
							void handleSubmit("express");
						}}
					/>

					{canUsePayPal && <BecomeAMemberCheckoutPayPalButton />}

					<button
						type="button"
						onClick={openNewPaymentMethod}
						className="flex w-full items-center justify-center gap-2 rounded-lg bg-slate-100 py-2.5 font-medium hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600"
					>
						<CreditCard /> Pay With Card
					</button>
				</>
			)}

			{canUsePayPal && hasActivePayPalSubscription && (
				<BecomeAMemberCheckoutPayPalButton />
			)}

			{isAddingNewPaymentMethod && (
				<>
					{!!paymentMethods?.length && (
						<button
							type="button"
							className="group mb-1 -ml-0.5 flex items-center gap-1 font-medium text-sky-500 text-sm hover:underline"
							onClick={() => {
								setIsAddingNewPaymentMethod(false);
								setSelectedPaymentMethod(defaultPaymentMethod ?? null);
							}}
						>
							<ChevronLeftIcon className="size-5 transition group-hover:-translate-x-0.5" />{" "}
							Back To All Payment Methods
						</button>
					)}

					{!paymentMethods?.length && !hasActivePayPalSubscription && (
						<>
							<ExpressCheckoutElement
								options={{
									layout: {
										overflow: "never",
										maxColumns: 1,
									},

									paymentMethodOrder: ["googlePay", "applePay"],
								}}
								onClick={({ resolve }) => {
									resolve({
										emailRequired: true,
										applePay: {
											recurringPaymentRequest: {
												paymentDescription: planConfig.price!.nickname!,
												managementURL:
													"https://brookbushinstitute.com/membership",
												regularBilling: {
													amount: convertToCents(getTotalAmountDue(planConfig)),
													label: planConfig.price!.nickname!,
													recurringPaymentIntervalUnit: planConfig.price
														?.recurring
														?.interval as ApplePayRecurringPaymentRequestIntervalUnit,
													recurringPaymentIntervalCount: 1,
												},
											},
										},
									});
								}}
								onConfirm={() => {
									void handleSubmit("express");
								}}
							/>

							{canUsePayPal && <BecomeAMemberCheckoutPayPalButton />}

							<div className="flex w-full items-center justify-center gap-4 text-slate-500 text-sm dark:text-slate-300">
								<div className="flex-1 border-t" />
								OR PAY WITH CARD
								<div className="flex-1 border-t" />
							</div>
						</>
					)}

					{isPaymentElementError && (
						<div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-sm dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
							<p>{getPaymentElementLoadErrorMessage({ canUsePayPal })}</p>
							<Button
								className="mt-2 w-full"
								type="button"
								onClick={retryPaymentElement}
							>
								Retry
							</Button>
						</div>
					)}

					{isPaymentElementLoading && (
						<div aria-busy="true" className="flex flex-col gap-3">
							<span className="sr-only">Loading card form…</span>
							<Skeleton className="h-11 w-full" />
							<div className="flex gap-3">
								<Skeleton className="h-11 flex-1" />
								<Skeleton className="h-11 flex-1" />
							</div>
						</div>
					)}

					{/* Collapsed, not hidden: Stripe reserves ~350px before ready and
					    must stay mounted so a slow load can still recover. */}
					<div
						className={clsx({
							"h-0 overflow-hidden": !isPaymentElementReady,
						})}
					>
						<PaymentElement
							key={paymentElementRemountKey}
							options={{
								layout: {
									type: "accordion",
									defaultCollapsed: false,
									radios: true,
									spacedAccordionItems: true,
								},
								paymentMethodOrder: ["card", "cashapp"],
								wallets: {
									applePay: "auto",
									googlePay: "auto",
								},
							}}
							onReady={handlePaymentElementReady}
							onLoadError={onPaymentElementLoadError}
						/>
					</div>

					{!isPaymentElementError && (
						<Button
							className="w-full"
							type="submit"
							disabled={
								loading || !stripe || !elements || !isPaymentElementReady
							}
						>
							{isPaymentElementReady ? payButtonLabel : "Loading card form…"}
						</Button>
					)}
				</>
			)}

			{errorMessage && (
				<div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-sm dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
					<p>{errorMessage}</p>
					{isAddingNewPaymentMethod && (
						<p className="mt-1 text-red-600/80 dark:text-red-300/80">
							Enter a different card below, or go back and choose another
							payment method.
						</p>
					)}
				</div>
			)}
		</form>
	);
};

export default BecomeAMemberCheckoutPaymentForm;

declare global {
	interface Window {
		uetq: any[];
	}
}
